/**
 * API 클라이언트 서비스
 * 
 * 백엔드 API와의 통신을 담당하는 중앙화된 HTTP 클라이언트 서비스입니다.
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { requestRefresh, assertNever } from '../auth/refreshCoordinator';
import { requestSmsReauth } from '../auth/reauthBus';

// === API 응답 타입 정의 ===
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

// === 사용자 관련 타입 ===
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  createdAt?: string;
  updatedAt?: string;
}

// === 헬스체크 관련 타입 ===
export interface HealthStatus {
  status: string;
  timestamp: string;
  services?: {
    database?: string;
    cache?: string;
    storage?: string;
  };
  version?: string;
}

// 🔴 CRITICAL: 항상 상대 경로 사용 (절대 경로 금지)
// 프론트엔드 package.json의 proxy 설정이 자동으로 localhost:3000으로 라우팅
// 프로덕션에서는 Nginx가 /api를 백엔드로 프록시
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// 토큰 관련 상수
export const AUTH_TOKEN_KEY = 'auth_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';

// 기본 설정으로 axios 인스턴스를 생성합니다.
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 120초 타임아웃 (주문 수집은 오래 걸릴 수 있음)
  validateStatus: (status) => {
    // 200-299 범위와 304 (Not Modified)를 성공으로 처리
    return (status >= 200 && status < 300) || status === 304;
  },
});

// 토큰 유틸리티 함수 (authService에 의존하지 않도록 직접 구현)
const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

const setAuthToken = (token: string): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

// === 인증 실패 공통 처리 (F11 · S4-4) ===
// axios 인터셉터와 fetchWithAuth 가 동일한 로그아웃 정책을 공유하도록 노출한다.
// 토큰 제거 후 로그인 페이지로 이동한다.
export const clearAuthAndRedirect = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // ⛔ 종전에는 위 둘만 지웠다 [부당한 로그아웃 제거 1단계].
  //    그래서 ① 다른 탭이 `session_expiry` 만 보고 있으면 **이 로그아웃을 모르고**
  //    ② 남은 `user_info` 때문에 로그인 화면에서 옛 사용자 정보가 잠깐 비친다.
  //    로그아웃은 네 열쇠를 **한 벌로** 지운다.
  localStorage.removeItem('user_info');
  localStorage.removeItem('session_expiry');
  // 이미 로그인 페이지면 리다이렉트 루프 방지
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// refresh_token 으로 access token 을 재발급한다.
// 성공 시 새 토큰을 localStorage 에 저장하고 새 access token 을 반환한다.
// 실패 시 null 을 반환한다(호출측이 로그아웃 처리).
export const refreshAuthToken = async (): Promise<string | null> => {
  const outcome = await requestRefresh();
  return outcome.kind === 'ok' ? outcome.token : null;
};

/**
 * 재발급 결과를 **이유까지** 돌려준다.
 *
 * ⛔ `refreshAuthToken` 은 실패를 전부 `null` 로 뭉갠다. 그것이 종전 결함의 뿌리다 —
 *    서버가 잠깐 흔들린 것과 재발급권이 만료된 것을 호출측이 구분할 수 없었다.
 *    새 코드는 이쪽을 쓴다. 옛 이름은 기존 호출부 호환을 위해 남겨 둔다.
 */
export const refreshAuthTokenDetailed = requestRefresh;

/**
 * API 요청 실패 에러 (2026-08-05 신설).
 *
 * 종전에는 `new Error(message)` 에 `statusCode`·`responseData` 만 붙여 던졌다.
 * `statusCode` 는 보존됐지만 **`response` 객체와 `url` 이 없어서**
 *   ① `error.response?.data?.error` 로 읽는 코드(18파일·87라인)가 전부 undefined 를 받아
 *      서버가 보낸 실패 사유 대신 일반 문구가 노출됐고 (예: UserManagementTab 구성원 저장 실패),
 *   ② 어느 요청이 실패했는지 로그로 알 수 없었다
 *      (알림 콘솔 오류가 429 임을 특정하는 데 오래 걸린 직접 원인).
 *
 * axios 관례(`response`·`status`)와 종전 관례(`statusCode`·`responseData`)를 **둘 다** 채운다.
 * 기존 소비자 하위호환을 지키면서, 새 코드가 어느 쪽으로 읽어도 값이 나오게 하는 것이 목적이다.
 */
export class ApiRequestError extends Error {
  /** HTTP 상태 — axios 관례 이름 */
  status?: number;
  /** HTTP 상태 — 종전 관례 이름 (7개 파일이 사용 중이므로 유지) */
  statusCode?: number;
  /** 원본 axios response (`error.response.data...` 를 읽는 기존 코드용) */
  response?: any;
  /** 응답 본문 — 종전 관례 이름 (유지) */
  responseData?: any;
  /** 실패한 요청 */
  url?: string;
  method?: string;
  /** 응답 본문의 code 우선, 없으면 axios code (ERR_NETWORK 등) */
  code?: string;
  /** 검증 상세 (백엔드 VALIDATION_ERROR.details) */
  details?: any;
  /** 응답 자체가 오지 않은 경우 (네트워크 끊김 등) */
  isNetworkError?: boolean;
  /** Rate limit 응답의 재시도 권고 초 */
  retryAfter?: number;

  constructor(message: string) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// === 표준화된 에러 처리 유틸리티 ===
const handleApiError = (error: any, context: string): never => {
  // 성능 개선을 위해 로그 제거

  // 🔴 Phase 2.5 Step 1: axios CanceledError는 그대로 throw (정상적인 취소)
  if (
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError' ||
    error.message?.includes('canceled') ||
    error.message?.includes('aborted')
  ) {
    throw error; // 요청 취소 에러는 그대로 전달
  }

  // 2026-08-05: `error.config` 가 없는 경우가 실제로 있다 —
  //   인증 실패 후 인터셉터가 토큰 갱신·강제 이동을 하면 진행 중 요청이 config 없이 reject 된다
  //   (브라우저 실측: url=- 로 비어 있었다). 호출측이 넘기는 context(`GET /notifications/counts`)를
  //   폴백으로 써서 «어느 요청이 실패했는지» 는 항상 남게 한다.
  const ctxSpace = context.indexOf(' ');
  const ctxMethod = ctxSpace > 0 ? context.slice(0, ctxSpace) : undefined;
  const ctxUrl = ctxSpace > 0 ? context.slice(ctxSpace + 1) : undefined;

  const requestUrl: string | undefined = error?.config?.url || ctxUrl;
  const requestMethod: string | undefined = error?.config?.method
    ? String(error.config.method).toUpperCase()
    : ctxMethod;

  // 네트워크 오류 처리 — 종전에는 status·code·url 이 전무한 맨 Error 였다
  if (!error.response) {
    const netErr = new ApiRequestError(`네트워크 연결 오류: ${context}`);
    netErr.isNetworkError = true;
    netErr.code = error?.code || 'ERR_NETWORK';
    netErr.url = requestUrl;
    netErr.method = requestMethod;
    throw netErr;
  }

  // HTTP 상태 코드별 처리
  const { status, data } = error.response;

  // 백엔드는 { error: '한글 메시지' } 형태로 응답하는 라우트가 많아
  // data?.message 만 참조하면 사용자에게 fallback 문구만 노출되는 회귀가 발생함.
  // string 우선, 그다음 객체.message 까지 안전하게 풀어냄.
  const extractMessage = (): string | undefined => {
    if (!data) return undefined;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
    if (data.error && typeof data.error.message === 'string') return data.error.message;
    return undefined;
  };

  // 검증 details·에러 code 등 구조화 정보를 에러 객체에 보존한다.
  // (예: 작업지시서 전환의 항목별 안내 — 백엔드 VALIDATION_ERROR.details 를 호출측이 활용)
  //
  // 2026-08-05: `response`·`status`·`url`·`method` 를 함께 붙인다.
  //   기존 필드(statusCode·responseData·code·details)는 그대로 유지 — 7개 파일이 쓰고 있다.
  const makeError = (fallback: string): ApiRequestError => {
    const err = new ApiRequestError(extractMessage() || fallback);
    err.statusCode = status;
    err.status = status;
    err.responseData = data;
    err.response = error.response;
    err.url = requestUrl;
    err.method = requestMethod;
    // 본문 code 가 없으면 axios code(ERR_BAD_REQUEST 등)라도 남긴다
    if (error?.code) err.code = error.code;
    // top-level code 보존 (예: { success:false, error:'...', code:'ALREADY_ISSUED' })
    if (data && typeof (data as any).code === 'string') err.code = (data as any).code;
    if (data && typeof data.error === 'object' && data.error) {
      if ((data.error as any).code) err.code = (data.error as any).code;
      if ((data.error as any).details) err.details = (data.error as any).details;
    }
    return err;
  };

  switch (status) {
    case 400:
      throw makeError('잘못된 요청입니다.');
    case 401:
      throw makeError('인증이 필요합니다.');
    case 403:
      throw makeError('접근 권한이 없습니다.');
    case 404:
      throw makeError('요청한 리소스를 찾을 수 없습니다.');
    case 409:
      throw makeError('데이터 충돌이 발생했습니다. 다시 시도해주세요.');
    // 413 은 nginx(client_max_body_size)가 앱에 닿기 전에 끊는 경우가 대부분이라
    // 본문이 HTML 이고 code 도 없다. 원인을 알 수 있는 문구로 고정한다.
    case 413:
      throw makeError('파일 용량이 너무 커서 업로드하지 못했습니다.');
    case 429: {
      // Rate Limit 처리 — 2026-08-05: makeError 로 통일해 response·url 도 함께 붙인다.
      //   (종전에는 이 분기만 별도로 Error 를 만들어 status 외 정보가 전무했고,
      //    그래서 알림 콘솔 오류가 429 라는 사실을 로그에서 알 수 없었다)
      const retryAfter = data?.error?.retryAfter || 60; // 기본 60초
      const minutes = Math.ceil(retryAfter / 60);
      const err = makeError(`요청 횟수가 초과되었습니다. ${minutes}분 후에 다시 시도해주세요.`);
      err.retryAfter = retryAfter;
      throw err;
    }
    case 500:
      throw makeError('서버 내부 오류가 발생했습니다.');
    default:
      throw makeError(`서버 오류가 발생했습니다. (${status})`);
  }
};

// 요청 인터셉터를 추가하여 모든 요청에 인증 토큰을 포함시킵니다.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // ⛔ 파일 업로드(FormData)는 기본 헤더 'application/json' 을 반드시 지운다 (2026-09-11 실사고).
    //    axios 1.x 는 Content-Type 에 application/json 이 있으면 FormData 를
    //    `JSON.stringify(formDataToJSON(data))` 로 바꿔 보낸다(dist/.../axios.cjs transformRequest).
    //    그러면 첨부 파일은 `{"audio":{}}` 로 뭉개져 **서버에 파일이 아예 도착하지 않고**,
    //    multer 는 multipart 가 아니므로 req.file 을 만들지 않아 400(«녹음 파일이 없습니다») 이 난다.
    //    미팅 회의록 녹음 업로드가 이 경로로 항상 실패했다.
    //    헤더를 지우면 브라우저가 boundary 까지 붙은 multipart/form-data 를 직접 설정한다.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      const h = config.headers as unknown as {
        delete?: (name: string) => void;
        [key: string]: unknown;
      };
      if (typeof h.delete === 'function') {
        h.delete('Content-Type'); // AxiosHeaders — 대소문자 무관하게 지운다
      } else {
        Object.keys(h).forEach((k) => {
          if (k.toLowerCase() === 'content-type') delete h[k];
        });
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 토큰 갱신 진행 중인 여부를 추적하는 변수
let isRefreshing = false;

// 토큰 갱신 중에 대기하는 요청 큐
//
// ⛔ 종전에는 resolve 콜백만 담았다. 그래서 **거절할 방법이 없었고**,
//    실패 경로는 큐를 비우지도 거절하지도 못한 채 빠져나갔다.
//    실패가 곧 로그아웃(=페이지 이동)이던 시절엔 안 보였지만,
//    «서버 사정이면 로그아웃하지 않는다» 로 바꾸는 순간
//    재워 둔 요청이 **영원히 안 깨어나** 화면이 조용히 멈춘다.
//    그래서 resolve 와 reject 를 **함께** 담는다 [부당한 로그아웃 제거 1단계].
type 대기자 = { resolve: (token: string) => void; reject: (reason: Error) => void };
let refreshSubscribers: 대기자[] = [];

// 토큰 갱신 후 대기 중인 모든 요청에 새 토큰을 적용
const onRefreshed = (token: string) => {
  const 목록 = refreshSubscribers;
  refreshSubscribers = [];          // ⛔ 먼저 비운다. 콜백이 또 등록해도 섞이지 않는다
  목록.forEach(({ resolve }) => resolve(token));
};

// 재발급이 실패했을 때 대기 중인 요청들을 **결말짓는다**
const rejectRefreshSubscribers = (reason: Error) => {
  const 목록 = refreshSubscribers;
  refreshSubscribers = [];
  목록.forEach(({ reject }) => reject(reason));
};

// 새 토큰으로 재요청을 위한 구독 추가
const addRefreshSubscriber = (w: 대기자) => {
  refreshSubscribers.push(w);
};

// 응답 인터셉터를 추가하여 오류 처리와 토큰 만료를 관리합니다.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 응답 데이터가 있고 success 필드가 false인 경우에도 그대로 반환
    // 이를 통해 API 호출 시 성공/실패 여부를 확인할 수 있음
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 Unauthorized 오류를 처리합니다.
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // ── 퇴사 처리된 계정 [퇴사자계정차단 S2] ────────────────────────────
      //    서버가 code:'ACCOUNT_RESIGNED' 로 답하면 **갱신을 시도하지 않고** 바로 로그아웃한다.
      //
      //    이 분기가 없으면 이렇게 된다:
      //      요청 401 → 자동 갱신 시도 → 갱신도 401(퇴사자라 서버가 막음) → 재시도 → …
      //    화면은 계속 «접근 권한이 없습니다» 만 띄우고 사용자는 이유를 모른 채 갇힌다.
      //    게다가 갱신 왕복이 반복돼 API 한도(300회/분)를 포화시켜 **다른 직원까지** 로그인이 막힌다.
      //
      //    ⛔ 로그인 요청 자체의 거부보다 **먼저** 본다. 로그인 응답에도 이 코드가 실리는데,
      //       아래 분기에서 그냥 reject 되면 사용자가 이유를 모른다.
      const blockedCode =
        error.response?.data?.code ?? error.response?.data?.error?.code;
      // 차단 사유가 셋이다 — 퇴사 · 가입 승인 대기 · 가입 거부.
      // 셋 다 «토큰을 지우고 로그인 화면으로» 가 맞다. 문구만 서버가 준 것을 그대로 쓴다.
      const 차단사유들 = ['ACCOUNT_RESIGNED', 'ACCOUNT_PENDING_APPROVAL', 'ACCOUNT_REJECTED'];
      if (차단사유들.includes(String(blockedCode))) {
        if (typeof window !== 'undefined') {
          // 로그인 화면에서 이유를 보여 주기 위해 남긴다. 다음 로그인 화면 진입 때 1회 소비된다.
          //
          // ⛔ 이미 로그인 화면에 있으면 저장하지 않는다 [자체심사 2026-08-20].
          //    그때는 화면 이동이 없어 아무도 이 값을 읽지 않고, 그대로 탭에 남는다.
          //    그러면 나중에 **다른 사람이 정상 로그인·로그아웃한 뒤** 로그인 화면에 들어올 때
          //    「퇴사 처리된 계정입니다」가 뜬다 — 멀쩡한 직원이 자기 계정을 의심하게 된다.
          //    로그인 화면에서의 안내는 로그인 폼이 응답으로 직접 띄운다.
          const 이미로그인화면 = window.location?.pathname === '/login';
          if (!이미로그인화면) {
            try {
              window.sessionStorage.setItem(
                'auth_blocked_reason',
                error.response?.data?.message
                  ?? error.response?.data?.error?.message
                  ?? '퇴사 처리된 계정입니다. 관리자에게 문의하세요.',
              );
            } catch {
              /* 저장에 실패해도 로그아웃 자체는 진행한다 */
            }
          } else {
            // 혹시 이전에 남아 있던 값이 있으면 여기서 치운다
            try { window.sessionStorage.removeItem('auth_blocked_reason'); } catch { /* noop */ }
          }
        }
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // 로그인 요청이 실패한 경우는 토큰 갱신을 시도하지 않음
      if (originalRequest.url === '/auth/login' || originalRequest.url === '/auth/register') {
        return Promise.reject(error);
      }

      // 토큰 갱신 요청 경로에서의 실패는 즉시 로그아웃
      if (originalRequest.url === '/auth/refresh') {
        // 토큰 갱신 실패 시 로그아웃 처리
        console.error('리프레시 토큰 자체가 만료되었습니다. 로그아웃 처리합니다.');
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // 리프레시 토큰 확인 추가
      const refreshToken = getRefreshToken();

      // 리프레시 토큰이 없는 경우 로그아웃 처리
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(new Error('No refresh token available'));
      }

      // 이미 토큰 갱신 중인 경우, 대기열에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            // ⛔ 실패해도 **반드시 결말이 난다.** 이것이 없으면 요청이 영원히 멈춘다.
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // ⛔ 재발급은 조정자 한 곳에서만 나간다. 여기서 직접 부르지 않는다.
      //    이유별 결과를 받아야 «서버 사정» 과 «재발급권 만료» 를 가를 수 있다.
      const outcome = await requestRefresh();
      isRefreshing = false;

      if (outcome.kind === 'ok') {
        originalRequest.headers.Authorization = `Bearer ${outcome.token}`;
        onRefreshed(outcome.token);
        return apiClient(originalRequest);
      }

      // ⛔ 여기부터는 전부 실패 경로다. **대기열을 반드시 정산한다.**
      //    종전에는 성공할 때만 깨웠다. 실패가 곧 로그아웃이던 때는 안 보였지만,
      //    아래 «서버 사정» 분기처럼 로그아웃하지 않는 길이 생기면
      //    재워 둔 요청들이 영영 안 깨어나 화면이 조용히 멈춘다.
      rejectRefreshSubscribers(
        new Error(
          outcome.kind === 'unavailable' ? '연결을 확인하는 중입니다'
            : outcome.kind === 'sms_required' ? '보안 확인이 필요합니다'
              : '인증이 만료되었습니다',
        ),
      );

      // ⛔ `switch` + `assertNever` 로 쓴다. `if` 사슬이면 갈래가 늘어도
      //    **타입 검사가 아무것도 막지 못하고** 조용히 마지막(로그아웃)으로 떨어진다.
      //    실제로 `sms_required` 를 더했을 때 타입 오류가 0건이었다(Codex [C-1]).
      switch (outcome.kind) {
        case 'unavailable':
          // ⛔ 서버 사정이다. **토큰을 지우지 않고** 이 요청만 실패시킨다.
          //    조정자가 뒤에서 다시 시도하고, 성공하면 화면이 알아서 이어진다.
          return Promise.reject(error);

        case 'sms_required':
          // ⛔ **로그아웃하지 않는다.** 재발급권은 멀쩡하고 SMS 만 다시 받으면 된다.
          //    전역 재인증 창이 이 신호를 받아 뜬다(`auth-reauth-bus`).
          requestSmsReauth({ challenge: outcome.challenge, phoneNumber: outcome.phoneNumber });
          return Promise.reject(error);

        case 'blocked':
          if (typeof window !== 'undefined') {
            // 계정이 막힌 경우는 로그인 화면에서 사유를 보여 준다 [퇴사자계정차단 S2].
            const 이미로그인화면 = window.location?.pathname === '/login';
            if (!이미로그인화면) {
              try {
                window.sessionStorage.setItem('auth_blocked_reason', outcome.reason);
              } catch { /* 저장 실패해도 로그아웃은 진행한다 */ }
            }
          }
          clearAuthAndRedirect();
          return Promise.reject(error);

        case 'invalid':
          // 재발급권이 쓸모없다. 로그아웃이 맞다.
          clearAuthAndRedirect();
          return Promise.reject(error);

        default:
          assertNever(outcome);
      }
    }

    return Promise.reject(error);
  },
);

// API 요청 함수들
export const api = {
  // GET 요청
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .get(url, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `GET ${url}`);
        throw error;
      });
  },

  // POST 요청
  post: <T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .post(url, data, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `POST ${url}`);
        throw error; // 에러를 다시 throw해야 함
      });
  },

  // PUT 요청
  put: <T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .put(url, data, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `PUT ${url}`);
        throw error;
      });
  },

  // DELETE 요청
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .delete(url, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `DELETE ${url}`);
        throw error;
      });
  },

  // PATCH 요청
  patch: <T, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .patch(url, data, config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `PATCH ${url}`);
        throw error; // 에러를 다시 throw해야 함
      });
  },

  // === 특수 메서드들 ===
  
  // 헬스체크 (백엔드가 없는 경우를 대비한 모킹)
  healthCheck: async (): Promise<ApiResponse<HealthStatus>> => {
    try {
      // 실제 백엔드가 있는 경우
      const response = await api.get<HealthStatus>('/health');
      
      // 백엔드 응답이 있으면 성공으로 처리
      if (response && (response.status === 'OK' || response.status)) {
        return {
          success: true,
          data: response,
          message: '헬스체크 성공'
        };
      } else {
        return {
          success: false,
          message: '백엔드 응답이 올바르지 않습니다',
          data: response
        };
      }
    } catch (error) {
      // 백엔드가 없는 경우 모킹된 응답 반환
      return {
        success: false,
        message: '백엔드 서버에 연결할 수 없습니다',
        data: {
          status: 'disconnected',
          timestamp: new Date().toISOString(),
          services: {
            database: 'unknown',
            cache: 'unknown',
            storage: 'unknown'
          },
          version: '0.0.0'
        }
      };
    }
  },

  // 현재 사용자 정보 가져오기 (인증된 사용자용)
  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: '인증 토큰이 없습니다'
        };
      }

      return await api.get<ApiResponse<User>>('/auth/me');
    } catch (error) {
      // mock 폴백 대신 에러 전파
      return { success: false, message: '사용자 정보를 가져올 수 없습니다' };
    }
  },
  
  // request 메서드 추가 (axios의 request 메서드를 래핑)
  request: async <T>(config: AxiosRequestConfig): Promise<T> => {
    return apiClient
      .request(config)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleApiError(error, `API 요청 (${config.method} ${config.url})`);
        throw error;
      });
  },
};

export default api;
